import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import './Analyse.css';
import api from '../services/api';

const CAT_LABELS = { B: '🔩 Objectdelen', C: '⚠️ Schadebeelden', '5': '🔍 Oorzaken', A: '🛠️ Activiteiten' };
const CAT_NAMEN = { B: 'Objectdelen', C: 'Schadebeelden', '5': 'Oorzaken', A: 'Activiteiten' };
const CAT_TYPE = { B: 'objectdeel', C: 'schadebeeld', '5': 'oorzaak', A: 'activiteit' };
const CAT_ORDER = ['B', 'C', '5', 'A'];
const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16','#f97316','#14b8a6','#a855f7','#0ea5e9','#eab308','#22c55e','#f43f5e'];
const MAAND_KORT = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];

function Donut({ data, onSelect }) {
  const [anim, setAnim] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnim(true), 60); return () => clearTimeout(t); }, [data]);
  const size = 170, thickness = 26, r = (size - thickness) / 2, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + d.value, 0);
  if (!data.length || total === 0) return <div className="geen-items">Geen data</div>;
  let offset = 0;
  const segs = data.map((d, i) => {
    const len = (d.value / total) * circ;
    const node = (<circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth={thickness} strokeDasharray={`${anim ? len : 0} ${circ}`} strokeDashoffset={-offset} transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: 'stroke-dasharray 0.7s cubic-bezier(.4,0,.2,1)' }} />);
    offset += len; return node;
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef2f7" strokeWidth={thickness} />
        {segs}
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="26" fontWeight="700" fill="#111827">{total}</text>
        <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#6b7280">totaal</text>
      </svg>
      <div style={{ flex: 1, minWidth: 150 }}>
        {data.map((d, i) => (
          <div key={i} className="donut-legend-row" onClick={() => onSelect && onSelect(d.label)} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 5, cursor: onSelect ? 'pointer' : 'default', padding: '2px 4px' }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{d.label}</span><strong>{d.value}</strong>
            <span style={{ color: '#6b7280', width: 42, textAlign: 'right' }}>{Math.round((d.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaandTrend({ rijen }) {
  if (!rijen.length) return <div className="geen-items">Geen data</div>;
  const max = Math.max(...rijen.map(r => r.aantal), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 180, paddingTop: 10 }}>
      {rijen.map((r, i) => {
        const m = parseInt((r.maand || '').slice(5, 7), 10);
        const label = MAAND_KORT[m - 1] || r.maand;
        const h = Math.round((r.aantal / max) * 130);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: r.aantal ? '#111827' : '#cbd5e1', marginBottom: 4 }}>{r.aantal}</div>
            <div style={{ width: '68%', height: h, minHeight: 2, background: r.aantal ? 'linear-gradient(180deg,#60a5fa,#3b82f6)' : '#e5e7eb', borderRadius: '6px 6px 0 0', transition: 'height .6s ease' }} />
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 6 }}>{label}</div>
          </div>
        );
      })}
    </div>
  );
}

const matchSel = (r, sel) => {
  if (!sel) return true;
  if (sel.type === 'objectdeel') return r.objectdeel === sel.waarde;
  if (sel.type === 'schadebeeld') return r.schadebeeld === sel.waarde;
  if (sel.type === 'oorzaak') return r.oorzaak === sel.waarde;
  if (sel.type === 'activiteit') return r.activiteit === sel.waarde;
  if (sel.type === 'object') return r.object_naam === sel.waarde;
  if (sel.type === 'combi') return r.objectdeel === sel.objectdeel && r.oorzaak === sel.oorzaak;
  return true;
};

const StoringAnalyse = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [objStoringen, setObjStoringen] = useState([]);
  const [objLaden, setObjLaden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('jaar');
  const [afdelingen, setAfdelingen] = useState([]);
  const [afdeling, setAfdeling] = useState('');
  const [exporting, setExporting] = useState(false);
  const [oorzaakGroepen, setOorzaakGroepen] = useState([]);
  const [toonGroepen, setToonGroepen] = useState(false);
  const [slijtageLijst, setSlijtageLijst] = useState([]);
  const GROEP_OPTIES = [
    { v: '', label: '— geen —' },
    { v: 'slijtage', label: 'Slijtage (vervangen)' },
    { v: 'reiniging', label: 'Reiniging (vaker reinigen)' },
    { v: 'afstelling', label: 'Afstelling (herafstellen)' },
    { v: 'extern', label: 'Extern (telt niet voor vervangen)' },
  ];
  useEffect(() => {
    api.get('/analyse/slijtage-lijst').then(r => setSlijtageLijst(r.data.data || [])).catch(() => {});
  }, []);

  const laadOorzaakGroepen = async () => {
    try { const r = await api.get('/analyse/oorzaak-groepen'); setOorzaakGroepen(r.data.data || []); } catch (e) {}
  };
  const zetGroep = async (optie_id, groep) => {
    setOorzaakGroepen(prev => prev.map(o => o.id === optie_id ? { ...o, groep } : o));
    try { await api.put('/analyse/oorzaak-groep', { optie_id, groep }); } catch (e) { alert('Kon niet opslaan'); }
  };
  const [sel, setSel] = useState(null);
  const [vergelijk, setVergelijk] = useState([]);
  const [radarLijst, setRadar] = useState([]);
  const [herhaalData, setHerhaalData] = useState([]);
  const [herhaalVeld, setHerhaalVeld] = useState('schadebeeld');
  const [herhaalDagen, setHerhaalDagen] = useState(30);
  const [herhaalMin, setHerhaalMin] = useState(2);
  const [weerData, setWeerData] = useState(null);
  const [weerRisico, setWeerRisico] = useState(null);
  const [oorzaakVerschuiving, setOorzaakVerschuiving] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setSel(null);
      const params = { periode };
      if (afdeling) params.afdeling_id = afdeling;
      const [res, det, verg, radar] = await Promise.all([
        api.get('/analyse/storingen', { params }),
        api.get('/analyse/storingen/export', { params }).catch(() => ({ data: { rows: [] } })),
        api.get('/analyse/storingen/vergelijk', { params: { periode } }).catch(() => ({ data: { rijen: [] } })),
        api.get('/analyse/storingen/probleemwissels', { params: afdeling ? { afdeling_id: afdeling } : {} }).catch(() => ({ data: { wissels: [] } }))
      ]);
      setData(res.data.data);
      setDetailRows(det.data.rows || []);
      setVergelijk(verg.data.rijen || []);
      setRadar(radar.data.wissels || []);
    } catch (e) { console.error('storing-analyse:', e); }
    finally { setLoading(false); }
  }, [periode, afdeling]);

  useEffect(() => { api.get('/afdelingen').then(r => setAfdelingen(r.data.afdelingen || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/analyse/weeranalyse').then(r => setWeerData(r.data)).catch(() => setWeerData(null)); }, []);
  useEffect(() => { api.get('/analyse/weerrisico').then(r => setWeerRisico(r.data)).catch(() => setWeerRisico(null)); }, []);
  useEffect(() => { api.get('/analyse/oorzaakverschuiving').then(r => setOorzaakVerschuiving(r.data)).catch(() => setOorzaakVerschuiving(null)); }, []);
  useEffect(() => {
    const p = new URLSearchParams({ veld: herhaalVeld, dagen: herhaalDagen, min: herhaalMin });
    if (afdeling) p.set('afdeling_id', afdeling);
    api.get('/analyse/herhaalstoringen?' + p.toString())
      .then(r => setHerhaalData(r.data.herhaalstoringen || []))
      .catch(() => setHerhaalData([]));
  }, [herhaalVeld, herhaalDagen, herhaalMin, afdeling]);

  useEffect(() => {
    if (sel && sel.type === 'object' && sel.object_id) {
      setObjLaden(true);
      api.get(`/analyse/object/${sel.object_id}/storingen`)
        .then(r => setObjStoringen(r.data.storingen || []))
        .catch(() => setObjStoringen([]))
        .finally(() => setObjLaden(false));
    } else { setObjStoringen([]); }
  }, [sel]);

  const exportCSV = async () => {
    try {
      setExporting(true);
      const rows = detailRows;
      const headers = ['Storing', 'Datum', 'Object', 'Categorie', 'Objectdeel', 'Code B', 'Schadebeeld', 'Code C', 'Oorzaak', 'Code 5', 'Activiteit', 'Code A'];
      const esc = (c) => `"${(c == null ? '' : String(c)).replace(/"/g, '""')}"`;
      const lines = [headers.map(esc).join(';')];
      rows.forEach(r => lines.push([r.id, (r.created_at || '').slice(0, 10), r.object_naam, r.object_categorie, r.objectdeel, r.code_objectdeel, r.schadebeeld, r.code_schadebeeld, r.oorzaak, r.code_oorzaak, r.activiteit, r.code_activiteit].map(esc).join(';')));
      const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `storing-analyse-${periode}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alert('Export mislukt'); }
    finally { setExporting(false); }
  };

  const exportXLSX = async () => {
    try {
      setExporting(true);
      const rows = detailRows;
      const d = data || {};
      const tot = d.totalen || {};
      const wb = XLSX.utils.book_new();
      const aoa = [];
      aoa.push(['WOMsys — Storing-analyse']);
      aoa.push(['Periode', periode, 'Geexporteerd', new Date().toLocaleString('nl-NL')]);
      aoa.push([]);
      aoa.push(['Totalen']);
      aoa.push(['Storingen', tot.storingen_totaal || 0]);
      aoa.push(['Gecategoriseerd', tot.gecategoriseerd || 0]);
      aoa.push(['Gem. hersteltijd (u)', (d.hersteltijdUren && d.hersteltijdUren.uren != null) ? d.hersteltijdUren.uren : '']);
      aoa.push([]);
      CAT_ORDER.forEach(k => {
        const lijst = (d.catalogi && d.catalogi[k]) || [];
        if (lijst.length) { aoa.push([CAT_NAMEN[k], 'Aantal']); lijst.forEach(r => aoa.push([r.waarde, r.aantal])); aoa.push([]); }
      });
      if ((d.risicoObjecten || []).length) {
        aoa.push(['Risico-objecten', 'Storingen', 'Laatste', 'Dagen geleden']);
        d.risicoObjecten.forEach(r => aoa.push([r.object_naam || '—', r.aantal, (r.laatste ? String(r.laatste).slice(0, 10) : ''), r.dagen_sinds]));
      }
      const ws1 = XLSX.utils.aoa_to_sheet(aoa);
      ws1['!cols'] = [{ wch: 28 }, { wch: 14 }, { wch: 18 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Samenvatting');
      const det = [['Storing', 'Datum', 'Object', 'Categorie', 'Objectdeel', 'Code B', 'Schadebeeld', 'Code C', 'Oorzaak', 'Code 5', 'Activiteit', 'Code A']];
      rows.forEach(r => det.push([r.id, (r.created_at || '').slice(0, 10), r.object_naam, r.object_categorie, r.objectdeel, r.code_objectdeel, r.schadebeeld, r.code_schadebeeld, r.oorzaak, r.code_oorzaak, r.activiteit, r.code_activiteit]));
      const ws2 = XLSX.utils.aoa_to_sheet(det);
      ws2['!cols'] = [{ wch: 8 }, { wch: 12 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 8 }, { wch: 22 }, { wch: 8 }, { wch: 18 }, { wch: 8 }, { wch: 22 }, { wch: 8 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Storingen');
      XLSX.writeFile(wb, `storing-analyse-${periode}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { alert('Excel-export mislukt'); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="analyse-loading"><div className="spinner"></div><p>Analyse laden...</p></div>;
  if (!data) return <div className="analyse-loading"><p>Kon data niet laden</p></div>;

  const SeizoenOorzaak = ({ data }) => {
    if (!data || !data.length) return <div className="geen-items">Geen data</div>;
    const maanden = [...new Set(data.map(d => d.maand))].sort();
    const oorzaken = [...new Set(data.map(d => d.oorzaak).filter(Boolean))];
    const kleuren = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];
    const kleurVan = {}; oorzaken.forEach((o, i) => { kleurVan[o] = kleuren[i % kleuren.length]; });
    const perMaand = {}; maanden.forEach(m => { perMaand[m] = {}; });
    let maxTotaal = 0;
    data.forEach(d => { if (!d.oorzaak) return; perMaand[d.maand][d.oorzaak] = (perMaand[d.maand][d.oorzaak] || 0) + d.aantal; });
    maanden.forEach(m => { const t = Object.values(perMaand[m]).reduce((a, b) => a + b, 0); if (t > maxTotaal) maxTotaal = t; });
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 180, marginBottom: 12 }}>
          {maanden.map(m => {
            const totaal = Object.values(perMaand[m]).reduce((a, b) => a + b, 0);
            const hoogte = maxTotaal ? (totaal / maxTotaal) * 160 : 0;
            return (
              <div key={m} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
                <div style={{ width: '100%', maxWidth: 48, height: hoogte, display: 'flex', flexDirection: 'column-reverse', borderRadius: '4px 4px 0 0', overflow: 'hidden' }}>
                  {oorzaken.map(o => {
                    const n = perMaand[m][o] || 0;
                    if (!n) return null;
                    return <div key={o} title={o + ': ' + n} style={{ height: (n / totaal) * hoogte, background: kleurVan[o] }} />;
                  })}
                </div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>{m.slice(5)}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12 }}>
          {oorzaken.map(o => (
            <span key={o} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 11, height: 11, borderRadius: 2, background: kleurVan[o], display: 'inline-block' }} />{o}
            </span>
          ))}
        </div>
      </div>
    );
  };
  const { catalogi = {}, topObjecten = [], terugkerend = [], totalen = {}, trendMaand = [], oorzaakObjectdeel = [], letOp = [], trendVorige = {}, hersteltijdUren = {}, risicoObjecten = [], seizoenOorzaak = [] } = data;

  let trendData = trendMaand;
  if (periode === 'jaar') {
    const jaar = new Date().getFullYear();
    const byM = {};
    trendMaand.forEach(r => { const m = parseInt((r.maand || '').slice(5, 7), 10); if (m) byM[m] = r.aantal; });
    trendData = [];
    for (let m = 1; m <= 12; m++) trendData.push({ maand: `${jaar}-${String(m).padStart(2, '0')}-01`, aantal: byM[m] || 0 });
  }

  const huidig = totalen.storingen_totaal || 0;
  const vorige = trendVorige && trendVorige.aantal != null ? Number(trendVorige.aantal) : null;
  let trendBadge = null;
  if (vorige != null && vorige > 0) {
    const pct = Math.round(((huidig - vorige) / vorige) * 100);
    if (pct === 0) trendBadge = <span style={{ fontSize: 12, marginLeft: 8, color: 'rgba(255,255,255,.8)' }}>▬ 0%</span>;
    else if (pct > 0) trendBadge = <span style={{ fontSize: 12, marginLeft: 8, color: '#fecaca' }}>▲ +{pct}%</span>;
    else trendBadge = <span style={{ fontSize: 12, marginLeft: 8, color: '#bbf7d0' }}>▼ {pct}%</span>;
  }

  const risicoBadge = (n) => {
    if (n >= 3) return <span className="badge rood">hoog</span>;
    if (n === 2) return <span className="badge oranje">middel</span>;
    return <span className="badge" style={{ background: '#e5e7eb', color: '#374151' }}>laag</span>;
  };

  const selRows = sel ? detailRows.filter(r => matchSel(r, sel)) : [];

  return (
    <div className="analyse-container">
      <style>{`@keyframes saFadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}} .sa-card{animation:saFadeUp .45s ease both} .donut-legend-row:hover{background:#eef2ff;border-radius:6px} .klik-rij{cursor:pointer} .klik-rij:hover{background:#eef2ff}`}</style>
      <div className="analyse-toolbar no-print">
        <div className="analyse-title"><h1>🔧 Storing-analyse</h1></div>
        <div className="analyse-filters">
          <select value={periode} onChange={e => setPeriode(e.target.value)}>
            <option value="week">Deze week</option><option value="maand">Deze maand</option><option value="kwartaal">Dit kwartaal</option><option value="jaar">Dit jaar</option>
          </select>
          <select value={afdeling} onChange={e => setAfdeling(e.target.value)}>
            <option value="">Alle afdelingen</option>
            {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
          </select>
          <button className="print-btn" onClick={exportXLSX} disabled={exporting} style={{ marginLeft: '8px' }}>{exporting ? '...' : '📊 Excel'}</button>
          <button className="print-btn" onClick={exportCSV} disabled={exporting} style={{ marginLeft: '8px' }}>📥 CSV</button>
          <button className="print-btn" onClick={() => window.print()} style={{ marginLeft: '8px' }}>🖨️ Print</button>
          <button className="print-btn" onClick={() => navigate('/signalering')} style={{ marginLeft: '8px' }}>🔔 Signalering</button>
          {/* heatmap-knop: verberg door deze regel te verwijderen */}
          <button className="print-btn" onClick={() => navigate('/heatmap')} style={{ marginLeft: '8px' }}>🗺️ Heatmap</button>
          <button className="print-btn" onClick={() => { setToonGroepen(true); laadOorzaakGroepen(); }} style={{ marginLeft: '8px' }}>⚙️ Oorzaak-groepen</button>
          <button className="print-btn" onClick={() => navigate('/maandverslag')} style={{ marginLeft: '8px' }}>📅 Maandverslag</button>
          <button className="print-btn" onClick={() => window.location.href = '/analyse'} style={{ marginLeft: '8px' }}>← Analyse</button>
        </div>
      </div>

      {sel && (
        <div className="kaart sa-card" style={{ borderLeft: '4px solid #4f46e5' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ margin: 0 }}>🔍 Selectie — {sel.label} <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 14 }}>({sel.object_id ? objStoringen.length : selRows.length} storingen)</span></h2>
            <button className="print-btn" onClick={() => setSel(null)}>✕ wissen</button>
          </div>
          {sel.object_id ? (
            objLaden ? <div className="geen-items">Laden…</div> :
            objStoringen.length === 0 ? <div className="geen-items">Geen storingen op dit object</div> : (
              <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {objStoringen.map(st => (
                  <div key={st.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 600 }}>{st.titel || 'Storing'}</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {st.bron && <span className="badge" style={{ background: '#eef2ff', color: '#4f46e5' }}>{st.bron}</span>}
                        <span className="badge" style={{ background: '#f3f4f6', color: '#374151' }}>{st.status || '—'}</span>
                        {(st.aangestuurde_afdeling_naam || st.doorgestuurd_afdeling_naam) && (
                          <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>➡️ {st.aangestuurde_afdeling_naam || st.doorgestuurd_afdeling_naam}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 6px' }}>
                      {(st.created_at || '').slice(0, 10)}{st.gemeld_door_naam ? ' · ' + st.gemeld_door_naam : ''}
                    </div>
                    {st.weer_temp != null && (() => {
                      const t = Number(st.weer_temp);
                      const neer = st.weer_neerslag != null ? Number(st.weer_neerslag) : 0;
                      const vorst = st.weer_vorst === true || st.weer_vorst === 't';
                      const nat = neer > 1;
                      const icoon = vorst ? '❄️' : nat ? '🌧️' : t >= 22 ? '☀️' : '🌤️';
                      const beschr = vorst ? 'vorst' : nat ? neer.toFixed(0) + 'mm regen' : 'droog';
                      return (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, background: vorst ? '#eff6ff' : nat ? '#f0f9ff' : '#fffbeb', color: vorst ? '#1e40af' : nat ? '#0369a1' : '#92400e', borderRadius: 6, padding: '2px 8px', marginBottom: 6 }}>
                          {icoon} {t.toFixed(0)}° · {beschr}
                        </div>
                      );
                    })()}
                    {st.beschrijving && <div style={{ fontSize: 13, whiteSpace: 'pre-line', marginBottom: 6 }}>{st.beschrijving}</div>}
                    {st.verslag && st.verslag.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: 12, background: '#f9fafb', borderRadius: 6, padding: '6px 8px' }}>
                        {st.verslag.map((v, vi) => <span key={vi}><strong>{v.label}:</strong> {v.waarde || '—'}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            selRows.length === 0 ? <div className="geen-items">Geen gecategoriseerde storingen in selectie</div> : (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table className="analyse-tabel">
                  <thead><tr><th>#</th><th>Datum</th><th>Object</th><th>Objectdeel</th><th>Schadebeeld</th><th>Oorzaak</th><th>Activiteit</th></tr></thead>
                  <tbody>{selRows.map((r, i) => (
                    <tr key={i}><td>{r.id}</td><td>{(r.created_at || '').slice(0, 10)}</td><td>{r.object_naam || '—'}</td><td>{r.objectdeel || '—'}</td><td>{r.schadebeeld || '—'}</td><td>{r.oorzaak || '—'}</td><td>{r.activiteit || '—'}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {radarLijst.length > 0 && (
        <div className="kaart sa-card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <h2>{'\ud83d\udce1'} Trend-radar {'\u2014'} laatste 30 dagen vs. de 30 dagen ervoor</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {radarLijst.map((wv) => {
              const adviesMap = {
                'Vervuiling': 'vaker reinigen',
                'Vervuild': 'vaker reinigen',
                'Speling': 'controle Spoor & Baan',
                'Slijtage': 'onderdeel vervangen',
                'Verlichting kapot': 'verlichting nalopen',
              };
              const advies = wv.dominante_oorzaak ? (adviesMap[wv.dominante_oorzaak] || null) : null;
              return (
              <div key={wv.id} onClick={() => setSel({ type: 'object', waarde: wv.naam, label: `Object: ${wv.naam}`, object_id: wv.id })} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: '#f8fafc', borderRadius: 8, cursor: 'pointer' }}>
                <span style={{ fontSize: 18 }}>
                  {wv.trend === 'stijgend' ? '\u2197\ufe0f' : wv.trend === 'dalend' ? '\u2198\ufe0f' : '\u27a1\ufe0f'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>{wv.naam}</strong>
                  <span style={{ color: '#6b7280', fontSize: 12.5 }}>
                    {wv.categorie ? ' \u00b7 ' + wv.categorie : ''}{wv.locatie ? ' \u00b7 ' + wv.locatie : ''}
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: wv.trend === 'stijgend' ? '#dc2626' : '#334155' }}>
                  {wv.laatste30}x
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>was {wv.vorige30}x</span>
                {parseInt(wv.open30, 10) > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '2px 7px' }}>
                    {wv.open30} open
                  </span>
                )}
                {wv.dominante_oorzaak && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>
                    {wv.dominante_oorzaak} {wv.oorzaak_aantal}x{advies ? ' → ' + advies : ''}
                  </span>
                )}
              </div>
              );
            })}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: '#94a3b8' }}>
            Objecten met 2+ storingen in de laatste 30 dagen. {'\u2197\ufe0f'} = vaker dan de periode ervoor {'\u2014'} kandidaat voor preventief onderhoud.
          </div>
        </div>
      )}

      {letOp.length > 0 && (
        <div className="kaart sa-card" style={{ borderLeft: '4px solid #ef4444' }}>
          <h2>🔴 Let op — objecten met meerdere storingen</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {letOp.map((o, i) => (
              <div key={i} className="klik-rij" onClick={() => setSel({ type: 'object', waarde: o.object_naam, label: `Object: ${o.object_naam}`, object_id: o.object_id })} style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
                <strong>{o.object_naam || '—'}</strong> <span style={{ color: '#6b7280' }}>· {o.categorie || '—'}</span> <span className="badge rood" style={{ marginLeft: 6 }}>{o.aantal}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-kaart blauw sa-card" title="t.o.v. vorige periode"><div className="stat-getal">{huidig}</div><div className="stat-label">Storingen {trendBadge}</div></div>
        <div className="stat-kaart groen sa-card"><div className="stat-getal">{totalen.gecategoriseerd || 0}</div><div className="stat-label">Gecategoriseerd</div></div>
        <div className="stat-kaart sa-card" style={{ background: 'linear-gradient(135deg,#8b5cf6,#7c3aed)' }}>
          <div className="stat-getal" style={{ color: '#fff' }}>{hersteltijdUren && hersteltijdUren.uren != null ? hersteltijdUren.uren : '—'}</div>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,.85)' }}>Gem. hersteltijd (u)</div>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 17 }}>{'⚠️'} Herhaalstoringen {'—'} reparatie hield mogelijk niet</h2>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14, fontSize: 13 }}>
          <label>Op:{' '}
            <select value={herhaalVeld} onChange={e => setHerhaalVeld(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db' }}>
              <option value="schadebeeld">Schadebeeld</option>
              <option value="oorzaak">Oorzaak</option>
            </select>
          </label>
          <label>Binnen{' '}
            <input type="number" min="1" max="365" value={herhaalDagen} onChange={e => setHerhaalDagen(e.target.value)} style={{ width: 60, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db' }} /> dagen
          </label>
          <label>Minimaal{' '}
            <input type="number" min="2" max="20" value={herhaalMin} onChange={e => setHerhaalMin(e.target.value)} style={{ width: 55, padding: '4px 8px', borderRadius: 6, border: '1px solid #d1d5db' }} /> keer
          </label>
        </div>
        {herhaalData.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Geen herhaalstoringen met deze instellingen.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}><th style={{ padding: '6px 8px' }}>Object</th><th>Categorie</th><th>{herhaalVeld === 'oorzaak' ? 'Oorzaak' : 'Schadebeeld'}</th><th>Aantal</th><th>Kortste tussen</th><th>Periode</th></tr></thead>
            <tbody>
              {herhaalData.map((h, idx) => (
                <tr key={idx} className="klik-rij" style={{ cursor: 'pointer', borderTop: '1px solid #f1f5f9' }} onClick={() => setSel({ type: 'object', waarde: h.object_naam, label: `Object: ${h.object_naam}`, object_id: h.object_id })}>
                  <td style={{ padding: '8px' }}><strong>{h.object_naam || '—'}</strong></td>
                  <td>{h.categorie || '—'}</td>
                  <td>{h.waarde}</td>
                  <td><span style={{ fontWeight: 700, color: '#dc2626' }}>{h.aantal}x</span></td>
                  <td>{h.min_gap != null ? h.min_gap + ' dg' : '—'}</td>
                  <td style={{ color: '#6b7280', fontSize: 12.5 }}>{h.eerste} → {h.laatste}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {weerRisico && weerRisico.risicos && weerRisico.risicos.length > 0 && (
        <div className="kaart sa-card" style={{ borderLeft: '4px solid #f59e0b' }}>
          <h2 style={{ margin: '0 0 4px' }}>{'\u26a0\ufe0f'} Weer-risico deze week</h2>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
            Op basis van de 7-daagse verwachting: {weerRisico.samenvatting.warmeDagen} warme, {weerRisico.samenvatting.drogeDagen} droge, {weerRisico.samenvatting.natteDagen} natte, {weerRisico.samenvatting.vorstDagen} vorstdag(en).
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {weerRisico.risicos.map((r, i) => (
              <div key={i} className="klik-rij" onClick={() => setSel({ type: 'object', waarde: r.object_naam, label: 'Object: ' + r.object_naam, object_id: r.object_id })}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, cursor: 'pointer' }}>
                <span style={{ fontSize: 20 }}>{r.trigger === 'vorst' ? '\u2744\ufe0f' : r.trigger === 'nat' ? '\U0001F327\ufe0f' : '\u2600\ufe0f'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#111827' }}>{r.object_naam} <span style={{ fontWeight: 400, color: '#92400e', fontSize: 13 }}>· {r.aantal}× {r.oorzaak}</span></div>
                  <div style={{ fontSize: 12.5, color: '#78350f' }}>{r.reden}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {oorzaakVerschuiving && (() => {
        const pk = oorzaakVerschuiving.perKwartaal || [];
        const versch = oorzaakVerschuiving.verschuivingen || [];
        if (!pk.length && !versch.length) return null;
        const jaren = [...new Set(pk.map(x => (x.periode || '').split('-Q')[0]).filter(Boolean))].sort();
        const periodes = [];
        jaren.forEach(j => { for (let q = 1; q <= 4; q++) periodes.push(j + '-Q' + q); });
        const oorzaken = [...new Set(pk.map(x => x.oorzaak).filter(Boolean))];
        const kleuren = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'];
        const kleurVan = {}; oorzaken.forEach((o, i) => { kleurVan[o] = kleuren[i % kleuren.length]; });
        const perP = {}; periodes.forEach(p => { perP[p] = {}; });
        pk.forEach(x => { if (x.oorzaak) perP[x.periode][x.oorzaak] = x.aantal; });
        return (
        <div className="kaart sa-card">
          <h2 style={{ margin: '0 0 12px' }}>{'🔄'} Oorzaak-verschuiving over tijd</h2>
          {periodes.length > 0 && (
            <>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>Oorzaak-verdeling per kwartaal {'\u2014'} verschuift vervuiling naar slijtage? (vloot-veroudering)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 12 }}>
                {periodes.map(p => {
                  const totaal = Object.values(perP[p]).reduce((a, b) => a + b, 0);
                  const dsize = 120, dth = 20, dr = (dsize - dth) / 2, dc = dsize / 2, dcirc = 2 * Math.PI * dr;
                  let off = 0;
                  return (
                    <div key={p} style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'center' }}>
                       <svg width={dsize} height={dsize} viewBox={'0 0 ' + dsize + ' ' + dsize}>
                        <circle cx={dc} cy={dc} r={dr} fill="none" stroke="#eef2f7" strokeWidth={dth} />
                        {totaal > 0 && oorzaken.map(o => {
                          const n = perP[p][o] || 0; if (!n) return null;
                          const len = (n / totaal) * dcirc;
                          const node = <circle key={o} cx={dc} cy={dc} r={dr} fill="none" stroke={kleurVan[o]} strokeWidth={dth} strokeDasharray={len + ' ' + dcirc} strokeDashoffset={-off} transform={'rotate(-90 ' + dc + ' ' + dc + ')'} />;
                          off += len; return node;
                        })}
                        <text x={dc} y={dc + 5} textAnchor="middle" fontSize="20" fontWeight="700" fill="#111827">{totaal}</text>
                      </svg>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 6 }}>{p}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, marginBottom: 16 }}>
                {oorzaken.map(o => <span key={o} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 11, height: 11, borderRadius: 2, background: kleurVan[o], display: 'inline-block' }} />{o}</span>)}
              </div>
            </>
          )}
          {versch.length > 0 && (
            <>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{'\u26a0\ufe0f'} Objecten die kantelen naar slijtage (mogelijk einde levensduur)</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}><th style={{ padding: '6px 8px' }}>Object</th><th>Categorie</th><th>Was</th><th>Nu</th><th>Slijtage</th></tr></thead>
                <tbody>
                  {versch.map((v, i) => (
                    <tr key={i} className="klik-rij" style={{ cursor: 'pointer', borderTop: '1px solid #f1f5f9' }} onClick={() => setSel({ type: 'object', waarde: v.object_naam, label: 'Object: ' + v.object_naam, object_id: v.object_id })}>
                      <td style={{ padding: '8px' }}><strong>{v.object_naam || '\u2014'}</strong></td>
                      <td>{v.categorie || '\u2014'}</td>
                      <td style={{ color: '#6b7280' }}>{v.van}</td>
                      <td style={{ fontWeight: 700, color: '#b45309' }}>{v.naar}</td>
                      <td>{v.slijt_eerste_pct}% {'\u2192'} <strong styll={{ color: '#dc2626' }}>{v.slijt_tweede_pct}%</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        );
      })()}
      {risicoObjecten.length > 0 && (
        <div className="kaart sa-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
          <h2>🔮 Risico-objecten — kans op nieuwe storing</h2>
          <table className="analyse-tabel">
            <thead><tr><th>Object</th><th>Categorie</th><th>Storingen</th><th>Score</th><th>Volgende verwacht</th><th>Restwerk</th><th>Trend</th><th>Laatste</th><th>Dagen geleden</th><th>Risico</th></tr></thead>
            <tbody>{risicoObjecten.map((r, i) => (<tr key={i} className="klik-rij" onClick={() => setSel({ type: 'object', waarde: r.object_naam, label: `Object: ${r.object_naam}`, object_id: r.object_id })}><td>{r.object_naam || '—'}</td><td>{r.categorie || '—'}</td><td>{r.aantal}</td><td>{(() => { const sc = r.score != null ? r.score : null; if (sc == null) return '—'; const kl = sc >= 70 ? '#16a34a' : sc >= 40 ? '#d97706' : '#dc2626'; const bg = sc >= 70 ? '#dcfce7' : sc >= 40 ? '#fef3c7' : '#fee2e2'; return <span style={{ fontWeight: 700, color: kl, background: bg, borderRadius: 6, padding: '2px 8px' }}>{sc}</span>; })()}</td><td>{(() => { if (!r.volgende_verwacht) return '—'; const dtv = r.dagen_tot_verwacht; if (dtv != null && dtv < 0) { return <span style={{ color: '#dc2626', fontWeight: 700 }}>Over tijd ({Math.abs(dtv)}d)</span>; } const soon = dtv != null && dtv <= 7; return <span style={{ color: soon ? '#dc2626' : '#334155', fontWeight: soon ? 700 : 400 }}>{r.volgende_verwacht}{soon ? ' ⚠️' : ''}</span>; })()}</td><td>{r.restwerk_open ? <span style={{ color: '#b45309', fontWeight: 700 }}>{r.restwerk_open}</span> : '—'}</td><td>{r.trend === 'stijgend' ? '↗️' : r.trend === 'dalend' ? '↘️' : '➡️'}</td><td>{r.laatste ? String(r.laatste).slice(0, 10) : '—'}</td><td>{r.dagen_sinds}</td><td>{risicoBadge(Number(r.aantal))}</td></tr>))}</tbody>
          </table>
        </div>
      )}

      <div className="kaart sa-card"><h2>📈 Storingen per maand</h2><MaandTrend rijen={trendData} /></div>
      <div className="kaart sa-card"><h2>{'🌤️'} Seizoenspatroon {'—'} storingen per maand per oorzaak</h2><SeizoenOorzaak data={seizoenOorzaak} /></div>
      {weerData && (() => {
        const vNorm = (weerData.vorst || []).find(v => v.vorst === false || v.vorst === 'f' || v.vorst === null);
        const vVorst = (weerData.vorst || []).find(v => v.vorst === true || v.vorst === 't');
        const nDroog = (weerData.nat || []).find(v => v.nat === false || v.nat === 'f' || v.nat === null);
        const nNat = (weerData.nat || []).find(v => v.nat === true || v.nat === 't');
        const buckets = weerData.buckets || [];
        const maxB = Math.max(1, ...buckets.map(b => b.storingen));
        const num = (x) => x != null ? Number(x) : 0;
        return (
        <div className="kaart sa-card">
          <h2>{'🌧️'} Weer-correlatie {'—'} wat voorspelt storingen</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
            <div style={{ background: '#eff6ff', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, color: '#1e40af', fontWeight: 700, marginBottom: 6 }}>{'❄️'} Vorst-effect</div>
              <div style={{ fontSize: 13, color: '#334155' }}>Bij vorst: <strong>{vVorst ? num(vVorst.gem_per_dag).toFixed(2) : '0'}</strong> storingen/dag</div>
              <div style={{ fontSize: 13, color: '#334155' }}>Normaal: <strong>{vNorm ? num(vNorm.gem_per_dag).toFixed(2) : '0'}</strong> storingen/dag</div>
              {vVorst && vNorm && num(vNorm.gem_per_dag) > 0 && (
                <div style={{ fontSize: 12, marginTop: 6, color: num(vVorst.gem_per_dag) > num(vNorm.gem_per_dag) ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                  {num(vVorst.gem_per_dag) > num(vNorm.gem_per_dag) ? '↗️ ' + Math.round((num(vVorst.gem_per_dag)/num(vNorm.gem_per_dag) - 1)*100) + '% meer bij vorst' : 'Geen verhoging bij vorst'}
                </div>
              )}
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, color: '#166534', fontWeight: 700, marginBottom: 6 }}>{'🌧️'} Regen-effect</div>
              <div style={{ fontSize: 13, color: '#334155' }}>Nat (&gt;1mm): <strong>{nNat ? num(nNat.gem_per_dag).toFixed(2) : '0'}</strong> /dag</div>
              <div style={{ fontSize: 13, color: '#334155' }}>Droog: <strong>{nDroog ? num(nDroog.gem_per_dag).toFixed(2) : '0'}</strong> /dag</div>
              {nNat && nDroog && num(nDroog.gem_per_dag) > 0 && (
                <div style={{ fontSize: 12, marginTop: 6, color: num(nNat.gem_per_dag) > num(nDroog.gem_per_dag) ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                  {num(nNat.gem_per_dag) > num(nDroog.gem_per_dag) ? '↗️ ' + Math.round((num(nNat.gem_per_dag)/num(nDroog.gem_per_dag) - 1)*100) + '% meer bij regen' : 'Meer bij droog weer'}
                </div>
              )}
            </div>
            <div style={{ background: '#fef9c3', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, color: '#854d0e', fontWeight: 700, marginBottom: 8 }}>{'🌡️'} Storingen per temperatuur</div>
              {buckets.map(b => (
                <div key={b.bucket} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ width: 44, fontSize: 12, color: '#334155' }}>{b.bucket}°</span>
                  <div style={{ flex: 1, background: '#fde68a', borderRadius: 4, height: 14 }}>
                    <div style={{ width: (b.storingen/maxB*100) + '%', background: '#f59e0b', height: '100%', borderRadius: 4 }} />
                  </div>
                  <span style={{ width: 26, fontSize: 12, fontWeight: 700, textAlign: 'right' }}>{b.storingen}</span>
                </div>
              ))}
            </div>
          </div>
          {(weerData.perOorzaak || []).length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ textAlign: 'left', color: '#6b7280', fontSize: 12 }}><th style={{ padding: '6px 8px' }}>Oorzaak</th><th>Aantal</th><th>Gem. temp</th><th>Gem. neerslag</th><th>Vorstdagen</th></tr></thead>
              <tbody>
                {weerData.perOorzaak.map((o, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '7px 8px' }}><strong>{o.oorzaak}</strong></td>
                    <td>{o.aantal}</td>
                    <td>{num(o.gem_temp).toFixed(1)}°C</td>
                    <td>{num(o.gem_neerslag).toFixed(1)} mm</td>
                    <td>{o.vorstdagen > 0 ? <span style={{ color: '#1e40af', fontWeight: 700 }}>{o.vorstdagen}❄️</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        );
      })()}

      {vergelijk.length > 0 && (() => {
        const maxS = Math.max(...vergelijk.map(v => v.storingen), 1);
        return (
          <div className="kaart sa-card">
            <h2>🏢 Afdeling-vergelijking</h2>
            <table className="analyse-tabel">
              <thead><tr><th>Afdeling</th><th style={{ width: '45%' }}>Storingen</th><th>Gecategoriseerd</th><th>Gem. hersteltijd (u)</th></tr></thead>
              <tbody>{vergelijk.map((v, i) => (
                <tr key={i}>
                  <td><strong>{v.naam}</strong></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, background: '#eef2f7', borderRadius: 6, height: 14, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.round((v.storingen / maxS) * 100)}%`, minWidth: 3, background: 'linear-gradient(90deg,#60a5fa,#3b82f6)', borderRadius: 6, transition: 'width .6s ease' }} />
                      </div>
                      <strong style={{ width: 28, textAlign: 'right' }}>{v.storingen}</strong>
                    </div>
                  </td>
                  <td>{v.gecategoriseerd}</td>
                  <td>{v.hersteltijd != null ? v.hersteltijd : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        {CAT_ORDER.map((k, idx) => (
          <div className="kaart sa-card" key={k} style={{ animationDelay: `${idx * 0.08}s` }}>
            <h2>{CAT_LABELS[k] || k}</h2>
            <Donut data={(catalogi[k] || []).map(r => ({ label: r.waarde, value: r.aantal }))} onSelect={(label) => setSel({ type: CAT_TYPE[k], waarde: label, label: `${CAT_NAMEN[k]}: ${label}` })} />
          </div>
        ))}
      </div>

      <div className="kaart sa-card">
        <h2>🔗 Oorzaak × objectdeel</h2>
        {oorzaakObjectdeel.length === 0 ? <div className="geen-items">Geen data</div> : (
          <table className="analyse-tabel"><thead><tr><th>Objectdeel</th><th>Oorzaak</th><th>Aantal</th></tr></thead>
            <tbody>{oorzaakObjectdeel.map((c, i) => <tr key={i} className="klik-rij" onClick={() => setSel({ type: 'combi', objectdeel: c.objectdeel, oorzaak: c.oorzaak, label: `${c.objectdeel} × ${c.oorzaak}` })}><td>{c.objectdeel}</td><td>{c.oorzaak}</td><td><span className="badge oranje">{c.aantal}</span></td></tr>)}</tbody></table>
        )}
      </div>

      <div className="kaart sa-card">
        <h2>🔁 Objecten met meeste storingen</h2>
        {topObjecten.length === 0 ? <div className="geen-items">Geen data</div> : (
          <table className="analyse-tabel"><thead><tr><th>Object</th><th>Categorie</th><th>Storingen</th></tr></thead>
            <tbody>{topObjecten.map((o, i) => <tr key={i} className="klik-rij" onClick={() => setSel({ type: 'object', waarde: o.object_naam, label: `Object: ${o.object_naam}`, object_id: o.object_id })}><td>{o.object_naam || '—'}</td><td>{o.categorie || '—'}</td><td><span className="badge oranje">{o.aantal}</span></td></tr>)}</tbody></table>
        )}
      </div>

      <div className="kaart sa-card">
        <h2>⚠️ Terugkerende storing per object</h2>
        {terugkerend.length === 0 ? <div className="geen-items">Nog geen terugkerende combinaties</div> : (
          <table className="analyse-tabel"><thead><tr><th>Object</th><th>Schadebeeld</th><th>Aantal</th></tr></thead>
            <tbody>{terugkerend.map((t, i) => <tr key={i} className="klik-rij" onClick={() => setSel({ type: 'object', waarde: t.object_naam, label: `Object: ${t.object_naam}`, object_id: t.object_id })}><td>{t.object_naam || '—'}</td><td>{t.schadebeeld}</td><td><span className="badge rood">{t.aantal}</span></td></tr>)}</tbody></table>
        )}
      </div>
      <div className="analyse-card" style={{ marginTop: 16, border: '2px solid #f59e0b', borderRadius: 10, padding: 16, background: 'white' }}>
        <h3 style={{ marginTop: 0 }}>🔧 Binnenkort vervangen — slijtage versnelt</h3>
        <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>Objecten met 3+ storingen uit de groep <b>slijtage</b>. "Versnelt" = het interval tussen storingen wordt korter (mogelijk einde levensduur). Externe oorzaken (vervoer, aanrijding) tellen hier niet mee.</p>
        {slijtageLijst.length === 0 ? (
          <p style={{ fontSize: 13, color: '#9ca3af' }}>Geen objecten met 3+ slijtage-storingen.</p>
        ) : (
          <table className="analyse-tabel">
            <thead><tr><th>Object</th><th>Categorie</th><th>Slijtage-storingen</th><th>Gem. interval</th><th>Recent interval</th><th>Signaal</th><th>Laatste</th></tr></thead>
            <tbody>
              {slijtageLijst.map((o, i) => (
                <tr key={i} className="klik-rij" onClick={() => setSel({ type: 'object', waarde: o.naam, label: 'Object: ' + o.naam, object_id: o.object_id })}>
                  <td><b>{o.naam}</b>{o.subcategorie ? ' · ' + o.subcategorie : ''}</td>
                  <td>{o.categorie || '—'}</td>
                  <td>{o.aantal}</td>
                  <td>{o.gem_interval_dagen} dgn</td>
                  <td>{o.recent_interval_dagen} dgn</td>
                  <td>{o.versnelt ? <span className="badge rood">⚠ versnelt</span> : <span className="badge">stabiel</span>}</td>
                  <td>{o.laatste_storing ? new Date(o.laatste_storing).toLocaleDateString('nl-NL') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {toonGroepen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setToonGroepen(false)}>
          <div style={{ background: 'white', borderRadius: 10, padding: 24, width: 560, maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>⚙️ Oorzaak-groepen instellen</h2>
              <button onClick={() => setToonGroepen(false)} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>Bepaal per oorzaak tot welke groep hij hoort. Alleen <b>slijtage</b> telt mee voor de vervangen-lijst; <b>extern</b> telt overal mee behalve daar.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {oorzaakGroepen.map(o => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '6px 8px', background: '#f9fafb', borderRadius: 6 }}>
                  <span style={{ fontSize: 13 }}>{o.waarde}</span>
                  <select value={o.groep || ''} onChange={e => zetGroep(o.id, e.target.value)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13 }}>
                    {GROEP_OPTIES.map(g => <option key={g.v} value={g.v}>{g.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoringAnalyse;
